import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography } from '@mui/material';

const ALLOWED_ELEMENTS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'p',
  'blockquote',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'code',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
  'br',
  'a',
  'img',
];

// Длинные неразрывные токены переносим: тело панели обрезает горизонтальный overflow (таблицы скроллятся отдельно)
const WRAP = { overflowWrap: 'anywhere' } as const;

const components: Components = {
  // Ссылки и картинки не рендерим — оставляем только текст (ссылки/alt)
  a: ({ children }) => <>{children}</>,
  img: ({ alt }) => <>{alt}</>,
  h1: ({ children }) => (
    <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 1, ...WRAP }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      variant="h6"
      component="h2"
      sx={{ mt: 3, mb: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider', ...WRAP }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle1" component="h3" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold', ...WRAP }}>
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="subtitle2" component="h4" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold', ...WRAP }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body2" component="p" sx={{ lineHeight: 1.6, my: 1, ...WRAP }}>
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <Typography variant="body2" component="li" sx={{ lineHeight: 1.6, ...WRAP }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 3, my: 1 }}>
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        m: 0,
        my: 1,
        px: 2,
        py: 0.5,
        ...WRAP,
        borderLeft: 4,
        borderColor: 'primary.main',
        bgcolor: (theme) => `${theme.palette.primary.main}14`,
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          '& tbody tr:nth-of-type(even)': { bgcolor: 'grey.50' },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  th: ({ children }) => (
    <Box
      component="th"
      sx={{
        border: 1,
        borderColor: 'divider',
        px: 1,
        py: 0.5,
        textAlign: 'left',
        bgcolor: 'grey.100',
        typography: 'body2',
        fontWeight: 'bold',
      }}
    >
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box
      component="td"
      sx={{
        border: 1,
        borderColor: 'divider',
        px: 1,
        py: 0.5,
        typography: 'body2',
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        bgcolor: 'grey.100',
        fontFamily: 'monospace',
        fontSize: '0.85em',
        px: 0.5,
        borderRadius: 0.5,
      }}
    >
      {children}
    </Box>
  ),
};

export const MarkdownView = ({ markdown }: { markdown: string }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    allowedElements={ALLOWED_ELEMENTS}
    unwrapDisallowed
    skipHtml
    components={components}
  >
    {markdown}
  </ReactMarkdown>
);
